// The arena client (lane C): one per arena room (WELCOME.arena), owned by app.ts. It glues the
// store (FRAME arena sections), the own-head predictor, the camera, the renderer and effects,
// the HUD, the controls and the arena sounds, and turns drained events into labels, the kill
// feed, break-ups and sounds at their render frame (stale ones never play; C-15, C-23).
//
// The app keeps the ui-flow state machine, the money flow, the socket and the results screen
// timing; it calls `applyFrame` per FRAME, `spawned` / `lifeEnded` at life edges and `frame`
// once per display frame. The classic FFA path never builds one.

import {
  ARENA_CAUSE_CODE,
  ARENA_CLIENT,
  ARENA_EVENT,
  type AmountKind,
  type FrameMessage,
  MONEY_DEFAULTS,
  type Rgb,
  type WelcomeArenaInfo,
} from '@ballio/shared';
import type { PlayOptions } from '../audio/engine.js';
import type { SoundSlot } from '../audio/recipes.js';
import { get2d } from '../render/canvas.js';
import {
  type ArenaRoomKind,
  isOnboardingSeen,
  loadArenaBest,
  markOnboardingSeen,
  type Settings,
} from '../settings/store.js';
import { t } from '../ui/i18n/t.js';
import { AlarmSound, classifyArenaEvents } from './audio.js';
import { ArenaCamera, type ArenaView, viewRect } from './camera.js';
import { ArenaControls } from './controls.js';
import { ArenaEffects } from './effects.js';
import { ArenaHud, type FeedLine, type FeedPart } from './hud.js';
import { arenaBoardRows, arenaFeedText, BannerQueue, formatPct, hudScale } from './hud-model.js';
import { OwnHeadPredictor } from './predict.js';
import { ArenaRenderer, type ArenaDrawHead } from './renderer.js';
import { shadesOf } from './shades.js';
import { ArenaStore, isStaleEvent, type QueuedArenaEvent } from './store.js';

export interface ArenaRoomFacts {
  /** A money or practice room (bags, cash-out). */
  readonly money: boolean;
  readonly practice: boolean;
  readonly cashOutKey: string;
  readonly cashOutHoldSec: number;
}

export interface ArenaClientDeps {
  readonly window: Window;
  readonly canvas: HTMLCanvasElement;
  /** The HUD layer (the arena HUD and the touch stick mount here). */
  readonly hudRoot: HTMLElement;
  readonly sound: { trigger(slot: SoundSlot, nowMs?: number, opts?: PlayOptions): boolean };
  readonly settings: () => Settings;
  /** Lamports → label text ("$12.40" / "0.08 SOL"), or null outside money rooms. */
  readonly formatValue: (lamports: number) => string | null;
  /** A board amount → compact text, or null (score boards show no bag). */
  readonly formatBoard: (amount: number, kind: AmountKind | undefined) => string | null;
  readonly room: () => ArenaRoomFacts;
  readonly sendHeading: (steps: number, seq: number) => void;
  readonly setCashOutHeld: (down: boolean) => void;
  /** Whether the cash-out key or button is held (the "Leave your land" notice). */
  readonly cashOutHeld: () => boolean;
  readonly touch: () => boolean;
  /** The nick shown on the own board row outside the top 5. */
  readonly ownName: () => string;
}

export interface ArenaFrameState {
  /** A life is being played with the menu closed (steering, HUD own block). */
  readonly playing: boolean;
  /** The arena is on screen at all (else nothing is drawn). */
  readonly visible: boolean;
  readonly widthPx: number;
  readonly heightPx: number;
  /** Canvas backing px per CSS px (the render quality scale; drawing only). */
  readonly k: number;
  /**
   * The screen's device pixel ratio (`window.devicePixelRatio`). The HUD sizes snap to whole
   * device px with it (SF-8); the canvas scale `k` follows the quality setting instead.
   */
  readonly dpr: number;
  readonly renderTick: number;
  readonly rttMs: number;
}

/**
 * One chewing chip per head at most this often (visuals §8, C-19: ≤ 60 per second whatever the
 * display rate; a 0.5 ms tolerance keeps a 60 Hz display at one per frame).
 */
const CHIP_PERIOD_MS = 1000 / 60 - 0.5;

const TIP_DELAY_MS = 600;
const TIP_MAX_MS = 12_000;
const TIP_FADE_MS = 300;

export class ArenaClient {
  readonly info: WelcomeArenaInfo;
  readonly store: ArenaStore;
  readonly camera: ArenaCamera;
  readonly predictor: OwnHeadPredictor;
  readonly renderer: ArenaRenderer;
  readonly effects = new ArenaEffects();
  readonly hud: ArenaHud;
  readonly controls: ArenaControls;
  private readonly deps: ArenaClientDeps;
  private readonly banners = new BannerQueue();
  private readonly alarm = new AlarmSound();
  /** A life is running (SPAWNED … DEATH / CASH_OUT_DONE). */
  private alive = false;
  private ownColor: Rgb = 0x8c8c8c;
  private ownHeadVersion = -1;
  private lastView: ArenaView | null = null;
  private killerId = 0;
  private wasShielded = false;
  private cashProgress = 0;
  private cashCancelArmed = true;
  private lifeStartMs = 0;
  private tip: HTMLElement | null = null;
  private tipShownAtMs = Number.NaN;
  private tipHideAtMs = Number.NaN;
  private bagLost = false;
  /** The own land % last seen while alive (the pill holds it through the death delay; SF-11). */
  private lastAlivePct = 0;
  private holdDown = false;
  /** Per head: when its last chewing chip spawned (the C-19 rate cap). */
  private readonly chipAtMs = new Map<number, number>();
  private readonly offs: (() => void)[] = [];

  constructor(info: WelcomeArenaInfo, tickRate: number, deps: ArenaClientDeps) {
    this.info = info;
    this.deps = deps;
    this.store = new ArenaStore(info);
    this.camera = new ArenaCamera(info);
    this.predictor = new OwnHeadPredictor(info, tickRate);
    this.renderer = new ArenaRenderer(this.store);
    const doc = deps.window.document;
    this.hud = new ArenaHud(doc, this.store);
    deps.hudRoot.append(this.hud.el);
    this.controls = new ArenaControls({
      window: deps.window,
      canvas: deps.canvas,
      overlayRoot: deps.hudRoot,
      sink: {
        sendHeading: (steps, seq) => {
          deps.sendHeading(steps, seq);
          this.predictor.noteInput(seq, steps, performance.now());
        },
        cashOut: (down) => deps.setCashOutHeld(down),
      },
      steerMode: () => deps.settings().arenaSteer,
    });
    this.controls.attach();
    // The touch hold button: press = CASH_OUT_START, release = CANCEL.
    const btn = this.hud.holdButton;
    const down = (ev: Event): void => {
      ev.preventDefault();
      this.holdDown = true;
      deps.setCashOutHeld(true);
    };
    const up = (): void => {
      if (!this.holdDown) return;
      this.holdDown = false;
      deps.setCashOutHeld(false);
    };
    btn.addEventListener('pointerdown', down);
    btn.addEventListener('pointerup', up);
    btn.addEventListener('pointercancel', up);
    btn.addEventListener('pointerleave', up);
    this.offs.push(() => {
      btn.removeEventListener('pointerdown', down);
      btn.removeEventListener('pointerup', up);
      btn.removeEventListener('pointercancel', up);
      btn.removeEventListener('pointerleave', up);
    });
  }

  /** The room kind for the stored best (UI §2.1). */
  roomKind(): ArenaRoomKind {
    const r = this.deps.room();
    return r.practice ? 'practice' : r.money ? 'money' : 'free';
  }

  get lifeActive(): boolean {
    return this.alive;
  }

  /** One FRAME (on arrival). */
  applyFrame(msg: FrameMessage, atMs: number): void {
    this.store.applyFrame(msg, atMs);
    const own = this.store.own;
    if (own.head && own.headVersion !== this.ownHeadVersion) {
      this.ownHeadVersion = own.headVersion;
      this.predictor.reconcile(own.head, own.inputSeq, atMs);
    }
  }

  /** SPAWNED: the new life is this player id. */
  spawned(playerId: number, nowMs: number): void {
    this.store.setOwn(playerId);
    this.predictor.reset();
    this.controls.resetLife();
    this.alive = true;
    this.bagLost = false;
    this.lastAlivePct = 0;
    this.killerId = 0;
    this.wasShielded = false;
    this.cashProgress = 0;
    this.cashCancelArmed = true;
    this.lifeStartMs = nowMs;
    this.ownHeadVersion = -1;
    this.camera.mode = 'follow';
    this.camera.snap();
    this.alarm.reset();
    this.banners.drop('finding', nowMs);
    this.scheduleTip(nowMs);
  }

  /** "Finding a spot…" while a PLAY waits for its spawn (spec §6 fallback). */
  finding(nowMs: number): void {
    this.banners.push(
      {
        key: 'finding',
        title: t('arena.notice.finding'),
        detail: t('arena.notice.findingDetail'),
        icon: 'info',
      },
      nowMs,
    );
  }

  /** The life ended (DEATH or CASH_OUT_DONE). The camera pans to the killer after a death. */
  lifeEnded(kind: 'death' | 'cashOut', killerId: number, nowMs: number): void {
    this.alive = false;
    this.killerId = kind === 'death' ? killerId : 0;
    this.bagLost = kind === 'death';
    // After a death the camera pans to the killer, or stays on the crash (the drops pop out).
    this.camera.mode = kind === 'death' ? 'death' : 'spectate';
    this.predictor.reset();
    this.controls.setEnabled(false);
    this.hideTip(nowMs);
    this.banners.clear();
  }

  /** After the results: the spectator view over the map centre. */
  spectate(): void {
    this.camera.mode = 'spectate';
    this.store.setOwn(0);
    this.killerId = 0;
    this.bagLost = false;
    this.lastAlivePct = 0;
  }

  /** NOTICE idleWarning in an arena room. */
  idleWarning(secs: number, nowMs: number): void {
    this.banners.push(
      {
        key: 'idle',
        title: t('arena.notice.idle'),
        detail: t('arena.notice.idleDetail', { s: secs }),
        icon: 'clock',
      },
      nowMs,
    );
  }

  /** One display frame. */
  frame(nowMs: number, dtMs: number, f: ArenaFrameState): void {
    this.controls.setEnabled(f.playing && this.alive);
    this.controls.frame(nowMs);
    // Remote land, trails, drops and events whose frame the render tick has reached (P-3).
    this.store.advance(f.renderTick);
    this.drainEvents(nowMs);
    const settings = this.deps.settings();
    const room = this.deps.room();
    const st = this.store;
    const ownId = st.own.playerId;
    if (ownId !== 0) this.ownColor = st.colorOf(ownId);

    // Own head, camera.
    let ownHead: ArenaDrawHead | null = null;
    if (this.alive && ownId !== 0) {
      const p = this.predictor.sample(nowMs, f.rttMs);
      if (p) ownHead = { playerId: ownId, x: p.x, y: p.y, heading: p.heading };
    }
    let target: { x: number; y: number } | null = ownHead;
    if (this.camera.mode === 'death' && this.killerId !== 0) {
      target = st.sampleHead(this.killerId, f.renderTick);
    }
    const view = this.camera.update(target, st.own.landCells, dtMs, f.widthPx, f.heightPx);
    this.lastView = view;

    // Draw.
    if (f.visible) {
      const ctx = get2d(this.deps.canvas);
      if (ctx) {
        this.effects.update(nowMs, dtMs);
        this.renderer.draw(ctx, {
          view,
          k: f.k,
          nowMs,
          renderTick: f.renderTick,
          ownHead,
          formatBag: (v) => (room.money ? this.deps.formatValue(v) : null),
          practice: room.practice,
          crownId: this.crownId(),
          hideNames: settings.hideNames,
          hideSkins: settings.hideSkins,
          ownAim: this.controls.target,
          effects: this.effects,
        });
        this.spawnChips(nowMs);
      }
    }

    // Flags → banners and sounds.
    const own = st.heads.get(ownId);
    if (this.alive && own) {
      if (own.shielded && !this.wasShielded) {
        this.banners.push(
          {
            key: 'shield',
            title: t('arena.notice.shieldOn'),
            detail: t('arena.notice.shieldOnDetail', { s: Math.round(this.info.shieldSec) }),
            icon: 'shield',
          },
          nowMs,
        );
      } else if (!own.shielded && this.wasShielded) {
        this.banners.push(
          {
            key: 'shieldOff',
            title: t('arena.notice.shieldOff'),
            detail: t('arena.notice.shieldOffDetail'),
            icon: 'shield',
          },
          nowMs,
        );
      }
      this.wasShielded = own.shielded;
      if (own.autopilot) {
        this.banners.push(
          {
            key: 'autopilot',
            title: t('arena.notice.autopilot'),
            detail: t('arena.notice.autopilotDetail'),
            icon: 'plug',
          },
          nowMs,
        );
      }
      // Cash-out: "Leave your land" while held at home; cashCancel once per sortie on a reset.
      const home = ownHead !== null && st.ownerAtPoint(ownHead.x, ownHead.y) === ownId;
      if (room.money && this.deps.cashOutHeld() && home) {
        this.banners.push(
          {
            key: 'leaveLand',
            title: t('arena.notice.leaveLand'),
            detail: t('arena.notice.leaveLandDetail'),
            icon: 'coin',
          },
          nowMs,
        );
      }
      const prog = own.cashingOut ? own.progress : 0;
      if (own.cashingOut && prog === 0 && this.cashProgress > 0.02 && this.cashCancelArmed) {
        this.deps.sound.trigger('cashCancel', nowMs);
        this.cashCancelArmed = false;
      }
      if (prog > 0.02) this.cashCancelArmed = true;
      this.cashProgress = prog;
    }
    if (settings.arenaAlarm && this.alive && this.alarm.update(st.own.alarm, nowMs)) {
      this.deps.sound.trigger('homeAlarm', nowMs);
    }
    const b = this.banners.frame(nowMs);
    if (b.started) this.deps.sound.trigger('notice', nowMs);

    // HUD.
    if (this.alive && st.own.landPct > 0) this.lastAlivePct = st.own.landPct;
    this.updateTip(nowMs);
    const lb = st.leaderboard;
    const barW = hudScale(f.widthPx, f.heightPx, f.dpr).W;
    const board = lb
      ? arenaBoardRows(
          lb.entries,
          this.alive ? lb.yourRank : 0,
          lb.yourAmount,
          lb.yourLandPct,
          this.deps.ownName(),
          barW,
          (a) => this.deps.formatBoard(a, lb.amountKind),
          (e) =>
            e === null || e.isYou
              ? ownId !== 0
                ? this.ownColor
                : null
              : e.playerId !== undefined && st.players.has(e.playerId)
                ? st.colorOf(e.playerId)
                : this.colorByName(e.name),
        )
      : [];
    const bag = room.money && ownId !== 0 ? st.valueOf(ownId) : null;
    this.hud.setVisible(f.visible);
    this.hud.update(
      {
        playing: f.playing && (this.alive || this.bagLost),
        ownColor: this.ownColor,
        // Through the death delay the pill keeps the value it had at death (SF-11).
        landPct: this.alive && st.own.landPct > 0 ? st.own.landPct : this.lastAlivePct,
        bestPct: loadArenaBest(this.roomKind()),
        bagText: room.money
          ? bag !== null
            ? this.deps.formatValue(bag)
            : this.deps.formatValue(0)
          : null,
        bagLost: this.bagLost,
        practice: room.practice,
        kills: st.own.kills,
        board,
        banner: b.frame,
        holdButton: this.deps.touch() && room.money && this.alive && f.playing,
        holdProgress: own?.cashingOut ? own.progress : null,
        minimapAll: settings.arenaMinimapAll,
        touch: this.deps.touch(),
      },
      nowMs,
      f.widthPx,
      f.heightPx,
      f.dpr,
    );
  }

  /** A board row's player colour by name (unique names among known players only). */
  private colorByName(name: string): Rgb | null {
    if (name === '') return null;
    let color: Rgb | null = null;
    for (const p of this.store.players.values()) {
      if (p.name !== name) continue;
      if (color !== null) return null;
      color = p.color;
    }
    return color;
  }

  /** The board's #1 by name (unique names only). */
  private crownId(): number {
    const top = this.store.leaderboard?.entries[0];
    if (!top || top.name === '') return 0;
    let id = 0;
    for (const p of this.store.players.values()) {
      if (p.name !== top.name || !this.store.heads.has(p.id)) continue;
      if (id !== 0) return 0;
      id = p.id;
    }
    return id;
  }

  private spawnChips(nowMs: number): void {
    const st = this.store;
    const last = this.chipAtMs;
    if (last.size > 256) last.clear();
    for (const hd of this.renderer.drawnHeads) {
      const o = st.ownerAtPoint(hd.x, hd.y);
      if (o === 0 || o === hd.playerId) continue;
      const prev = last.get(hd.playerId);
      if (prev !== undefined && nowMs - prev < CHIP_PERIOD_MS && nowMs >= prev) continue;
      last.set(hd.playerId, nowMs);
      this.effects.chip(hd.x, hd.y, hd.heading, st.colorOf(o), nowMs);
    }
  }

  private inView(x: number, y: number): boolean {
    const v = this.lastView;
    if (!v) return false;
    const r = viewRect(v, 2);
    return x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;
  }

  private playerInView(id: number): boolean {
    const hd = this.store.heads.get(id);
    const s = hd?.samples[hd.samples.length - 1];
    if (s && this.inView(s.x, s.y)) return true;
    const tr = this.store.trailOf(id);
    const cols = this.store.cols;
    for (let i = 0; i < tr.length; i += 4) {
      const c = tr[i] as number;
      if (this.inView((c % cols) + 0.5, Math.floor(c / cols) + 0.5)) return true;
    }
    return false;
  }

  /** Drained events → effects, labels, feed and sounds (stale ones only free their snapshots). */
  private drainEvents(nowMs: number): void {
    const st = this.store;
    const queued = st.takeEvents();
    st.takeRemovedDrops();
    if (queued.length === 0) return;
    const ownId = st.own.playerId;
    const room = this.deps.room();
    // Group by arrival (one FRAME), so capture-then-kill spacing applies per frame.
    let i = 0;
    while (i < queued.length) {
      const atMs = (queued[i] as QueuedArenaEvent).atMs;
      const batch: QueuedArenaEvent[] = [];
      while (i < queued.length && (queued[i] as QueuedArenaEvent).atMs === atMs)
        batch.push(queued[i++] as QueuedArenaEvent);
      const stale = isStaleEvent(atMs, nowMs);
      for (const q of batch) this.applyEvent(q, stale, nowMs, ownId, room);
      if (stale) continue;
      const cues = classifyArenaEvents(
        batch.map((q) => q.event),
        {
          ownId: this.alive || batch.some((q) => q.event.type === ARENA_EVENT.DEATH) ? ownId : 0,
          playableCells: this.info.playableCells,
          money: room.money,
          inView: (id) => this.playerInView(id),
        },
        nowMs - atMs,
      );
      for (const c of cues) {
        const opts: PlayOptions =
          c.rate !== undefined ? { delayMs: c.delayMs, rate: c.rate } : { delayMs: c.delayMs };
        this.deps.sound.trigger(c.slot, nowMs, opts);
      }
    }
  }

  private applyEvent(
    q: QueuedArenaEvent,
    stale: boolean,
    nowMs: number,
    ownId: number,
    room: ArenaRoomFacts,
  ): void {
    const st = this.store;
    const e = q.event;
    switch (e.type) {
      case ARENA_EVENT.CAPTURE: {
        if (stale || e.playerId !== ownId || ownId === 0) return;
        const pct = (e.gainedCells / this.info.playableCells) * 100;
        if (pct >= ARENA_CLIENT.captureLabelMinPct) {
          this.effects.label(`+${formatPct(pct)}`, shadesOf(this.ownColor).base, nowMs);
        }
        const gain = st.ownGain;
        if (gain && gain.atMs === q.atMs) this.effects.captureFlash(gain.cells, st.cols, nowMs);
        this.hideTip(nowMs);
        return;
      }
      case ARENA_EVENT.DEATH: {
        const snap = st.takeDeathSnapshot(e.victimId);
        if (stale) return;
        if (snap) {
          const trail: number[] = [];
          for (const c of snap.trail)
            trail.push((c % st.cols) + 0.5, Math.floor(c / st.cols) + 0.5);
          this.effects.burst(snap.edges, trail, snap.color, nowMs, (x, y) => this.inView(x, y));
        }
        if (ownId !== 0 && e.killerId === ownId && e.victimId !== ownId) {
          this.effects.label(t('arena.float.kill'), st.colorOf(e.victimId), nowMs);
          const usd = room.money && e.value > 0 ? this.deps.formatValue(e.value) : null;
          if (usd) this.effects.label(`+${usd}`, ARENA_CLIENT.colors.bag, nowMs);
        }
        if (e.victimId === ownId && e.killerId !== 0) this.killerId = e.killerId;
        this.pushFeed(e.cause, e.killerId, e.victimId, e.value, ownId, nowMs, room);
        return;
      }
      case ARENA_EVENT.EXIT: {
        // A bank or forced exit: the land fades out instead of popping (UI §4.3; no break-up).
        const snap = st.takeExitSnapshot(e.playerId);
        if (stale || !snap) return;
        this.effects.landFade(snap.cells, st.cols, snap.color, nowMs);
        return;
      }
      case ARENA_EVENT.COLLECT: {
        if (stale || e.playerId !== ownId || ownId === 0) return;
        const usd = room.money ? this.deps.formatValue(e.value) : null;
        if (usd) this.effects.label(`+${usd}`, ARENA_CLIENT.colors.bag, nowMs, 800);
        return;
      }
      default:
        return;
    }
  }

  private pushFeed(
    cause: number,
    killerId: number,
    victimId: number,
    value: number,
    ownId: number,
    nowMs: number,
    room: ArenaRoomFacts,
  ): void {
    const st = this.store;
    const usd = room.money ? (this.deps.formatValue(value) ?? '') : '';
    if (!room.money) return; // the feed is for money transparency (UI §2.4)
    const killer = st.nameOf(killerId);
    const victim = st.nameOf(victimId);
    const text = arenaFeedText(cause, killer, victim, usd);
    // Split the line into name / money spans around the names and the value.
    const parts: FeedPart[] = [];
    let rest = text;
    const take = (needle: string, part: FeedPart): void => {
      if (needle === '') return;
      const at = rest.indexOf(needle);
      if (at < 0) return;
      if (at > 0) parts.push({ text: rest.slice(0, at), kind: 'text' });
      parts.push(part);
      rest = rest.slice(at + needle.length);
    };
    const self = cause === ARENA_CAUSE_CODE.SELF || killerId === 0;
    if (!self)
      take(killer || t('arena.unnamed'), {
        text: killer || t('arena.unnamed'),
        kind: 'name',
        color: st.colorOf(killerId),
      });
    take(victim || t('arena.unnamed'), {
      text: victim || t('arena.unnamed'),
      kind: 'name',
      color: st.colorOf(victimId),
    });
    take(usd, { text: usd, kind: 'money' });
    if (rest !== '') parts.push({ text: rest, kind: 'text' });
    const line: FeedLine = {
      parts,
      own: ownId !== 0 && (killerId === ownId || victimId === ownId),
      bornMs: nowMs,
    };
    this.hud.pushFeed(line);
  }

  // --- the one-time tip (spec §11, UI §7) -----------------------------------------------------

  private scheduleTip(nowMs: number): void {
    if (isOnboardingSeen('arena')) return;
    markOnboardingSeen('arena');
    this.tipShownAtMs = nowMs + TIP_DELAY_MS;
    this.tipHideAtMs = this.tipShownAtMs + TIP_MAX_MS;
    if (!this.tip) this.tip = this.buildTip();
  }

  private hideTip(nowMs: number): void {
    if (!Number.isFinite(this.tipHideAtMs)) return;
    if (nowMs < this.tipShownAtMs) {
      this.tipShownAtMs = Number.NaN;
      this.tipHideAtMs = Number.NaN;
      if (this.tip) this.tip.hidden = true;
      return;
    }
    this.tipHideAtMs = Math.min(this.tipHideAtMs, nowMs);
  }

  private updateTip(nowMs: number): void {
    const tip = this.tip;
    if (!tip) return;
    if (!Number.isFinite(this.tipShownAtMs) || nowMs < this.tipShownAtMs) {
      tip.hidden = true;
      return;
    }
    const out = nowMs - this.tipHideAtMs;
    if (out >= TIP_FADE_MS) {
      tip.hidden = true;
      this.tipShownAtMs = Number.NaN;
      this.tipHideAtMs = Number.NaN;
      return;
    }
    tip.hidden = false;
    const inA = Math.min(1, (nowMs - this.tipShownAtMs) / TIP_FADE_MS);
    const outA = out > 0 ? 1 - out / TIP_FADE_MS : 1;
    tip.style.opacity = Math.min(inA, outA).toFixed(3);
  }

  private buildTip(): HTMLElement {
    const doc = this.deps.window.document;
    const room = this.deps.room();
    const touch = this.deps.touch();
    const tip = doc.createElement('div');
    tip.className = 'arena-tip';
    tip.hidden = true;
    const title = doc.createElement('div');
    title.className = 'arena-tip-title';
    title.textContent = t('arena.onboard.title');
    const list = doc.createElement('ul');
    const rows = [
      touch ? t('arena.onboard.steerTouch') : t('arena.onboard.steer'),
      t('arena.onboard.loop'),
      t('arena.onboard.trail'),
    ];
    if (room.money) {
      const s = Math.round(room.cashOutHoldSec || MONEY_DEFAULTS.cashOutHoldSec);
      rows.push(
        touch
          ? t('arena.onboard.cashTouch', { s })
          : t('arena.onboard.cash', { key: room.cashOutKey, s }),
      );
    }
    for (const r of rows) {
      const li = doc.createElement('li');
      li.textContent = r;
      list.append(li);
    }
    tip.append(title, list);
    this.hud.el.append(tip);
    return tip;
  }

  /** Forget the room (a new socket). */
  destroy(): void {
    for (const off of this.offs.splice(0)) off();
    this.controls.detach();
    this.hud.destroy();
    this.tip?.remove();
  }

  /** Time alive (ms) of the current life (HUD and results fallbacks). */
  lifeMs(nowMs: number): number {
    return this.alive ? nowMs - this.lifeStartMs : 0;
  }
}
